import { useQuery } from "@tanstack/react-query";
import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";

export interface UnmappedScenario {
  id: string;
  scenario: string;
}

export interface UnmappedScenariosResponse {
  data: UnmappedScenario[];
  total: number;
}

interface GetUnmappedScenariosParams {
  projectUuid: string;
  page?: number;
  limit?: number;
}

const getUnmappedScenarios = async ({
  projectUuid,
  page = 1,
  limit = 50,
  headers,
}: GetUnmappedScenariosParams & {
  headers: Record<string, string>;
}): Promise<UnmappedScenariosResponse> => {
  const url = `${config.isometricApiUrl}/design-ontology-graph/${projectUuid}/scenarios/unmapped?page=${page}&limit=${limit}`;

  const response = await apiFetch(url, { headers });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch unmapped scenarios: ${response.statusText}`,
    );
  }

  const result: any = await response.json();

  // Normalize across possible response shapes ([], {data}, {items})
  const rawItems: any[] = Array.isArray(result)
    ? result
    : (result?.data ?? result?.items ?? []);

  const data: UnmappedScenario[] = rawItems
    .filter((item) => item && item.id)
    .map((item) => ({
      id: item.id,
      scenario: item.scenario ?? item.name ?? item.title ?? item.id,
    }));

  return { data, total: result?.total ?? data.length };
};

export const useGetUnmappedScenariosService = ({
  projectUuid,
  page = 1,
  limit = 50,
  enabled,
}: GetUnmappedScenariosParams & { enabled: boolean }) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["unmapped-scenarios", projectUuid, page, limit],
    queryFn: () =>
      getUnmappedScenarios({
        projectUuid,
        page,
        limit,
        headers: getAuthHeaders(),
      }),
    enabled: enabled && !!projectUuid,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });
};